import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useCallback, useState } from "react";
import { BlockerFunction, Location, useBlocker, useLocation } from "react-router-dom";
import { Routes } from "routes";
import { isGuid } from "utils";

/**
 * Guard to prevent user from navigating away from cart with items
 */
export function useBookingGuard() {
    const bookingId = useBookingId();
    const { isActivityBooking, isWaitList } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const location = useLocation() as TypedHistory;

    // Keep and expose nextLocation to be used together with guard dialog component
    const [nextLocation, setNextLocation] = useState<Location | null>(null);

    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) => {
            // Reset next location initially
            setNextLocation(null);

            const typed = nextLocation as TypedHistory;

            const isNotUndefined = typed.state?.intercept !== undefined;
            if (!typed.state?.intercept && isNotUndefined) return false;

            const isCurrentFinalize = currentLocation.pathname.includes(Routes.FINALIZE);
            const isNextAvailability = nextLocation.pathname.includes(Routes.AVAILABILITY);
            const navigatingBackFromFinalize = isCurrentFinalize && isNextAvailability;

            if (isActivityBooking && isWaitList && navigatingBackFromFinalize) return true;

            // Do not allow navigation away if booking is finalized
            const isFinal = location.state?.isFinal;
            if (booking?.isFinalized() && isFinal) return true;

            if (!bookingId) return false;

            const nextPath = nextLocation.pathname.split("/");

            if (!nextPath.some((path) => isGuid(path))) {
                setNextLocation(nextLocation);
                return true;
            }

            return false;
        },
        [isActivityBooking, isWaitList, location.state?.isFinal, booking, bookingId]
    );

    const blocker = useBlocker(shouldBlock);

    return { blocker, nextLocation };
}
